import { useState } from 'react'
import {
  App, Button, Form, Input, InputNumber, Modal, Select, Space, Switch,
  Tabs, Tag,
} from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  failureReasonsApi, pmTemplatesApi, sparesApi,
} from '@/features/maintenance/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const FAILURE_CATEGORIES = ['MECHANICAL', 'ELECTRICAL', 'UTILITY', 'SAFETY', 'SETTING', 'OTHER']
const SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']
const FREQUENCIES = ['DAILY', 'WEEKLY', 'FORTNIGHTLY', 'MONTHLY', 'QUARTERLY', 'HALF_YEARLY', 'ANNUAL', 'SHUTDOWN']
const MACHINE_TYPES = [
  'BLOW ROOM', 'CARDING', 'DRAW FRAME', 'COMBER', 'LAP FORMER',
  'SIMPLEX', 'RING FRAME', 'YCP', 'AC', 'HUMIDIFICATION',
  'COMPRESSOR', 'ELECTRICAL PANEL',
]

/**
 * Spec §G — Admin masters tabbed page.
 * Failure Reason · Spare Master · PM Template.
 * Heavy bulk-import, criticality, and approval matrix work is a Phase-3 polish.
 */
export default function MaintenanceMastersPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canCreate = usePermission('maintenance.create')

  // ── Failure reasons ───────────────────────────────────────────────────
  const { data: failureReasons, isLoading: frLoading } = useQuery({
    queryKey: ['failure-reasons'],
    queryFn: () => failureReasonsApi.list(),
  })
  const [frOpen, setFrOpen] = useState(false)
  const [frForm] = Form.useForm()
  const frMutation = useMutation({
    mutationFn: (vals: Record<string, unknown>) => failureReasonsApi.create(vals),
    onSuccess: () => {
      message.success('Failure reason added')
      qc.invalidateQueries({ queryKey: ['failure-reasons'] })
      setFrOpen(false)
    },
  })

  // ── Spare master ─────────────────────────────────────────────────────
  const [spSearch, setSpSearch] = useState('')
  const { data: spares, isLoading: spLoading } = useQuery({
    queryKey: ['spares-list-master', spSearch],
    queryFn: () => sparesApi.list({ limit: 1000, search: spSearch || undefined }),
  })
  const [spOpen, setSpOpen] = useState(false)
  const [spForm] = Form.useForm()
  const spMutation = useMutation({
    mutationFn: (vals: Record<string, unknown>) => sparesApi.create(vals),
    onSuccess: () => {
      message.success('Spare added')
      qc.invalidateQueries({ queryKey: ['spares-list-master'] })
      qc.invalidateQueries({ queryKey: ['spares-list'] })
      setSpOpen(false)
    },
  })

  // ── PM templates ─────────────────────────────────────────────────────
  const { data: templates, isLoading: tmLoading } = useQuery({
    queryKey: ['pm-templates'],
    queryFn: () => pmTemplatesApi.list(),
  })
  const [tmOpen, setTmOpen] = useState(false)
  const [tmForm] = Form.useForm()
  const tmMutation = useMutation({
    mutationFn: (vals: { template_code: string; template_name: string; machine_type: string; frequency: string; estimated_duration_minutes?: number; safety_critical?: boolean; checklist_text?: string }) => {
      const checklist = (vals.checklist_text ?? '')
        .split('\n').map((l) => l.trim()).filter(Boolean)
        .map((line) => ({ item: line, input_type: 'checkbox', mandatory: true }))
      return pmTemplatesApi.create({
        template_code: vals.template_code,
        template_name: vals.template_name,
        machine_type: vals.machine_type,
        frequency: vals.frequency,
        estimated_duration_minutes: vals.estimated_duration_minutes,
        safety_critical: vals.safety_critical,
        checklist_json: checklist,
      })
    },
    onSuccess: () => {
      message.success('PM template added')
      qc.invalidateQueries({ queryKey: ['pm-templates'] })
      setTmOpen(false)
    },
  })

  return (
    <div>
      <PageHeader
        title="Maintenance Masters"
        subtitle="Failure reasons · Spare master · PM templates"
      />

      <Tabs
        items={[
          {
            key: 'fr', label: 'Failure reasons',
            children: (
              <>
                <Space style={{ marginBottom: 12 }}>
                  {canCreate && (
                    <Button icon={<PlusOutlined />} type="primary"
                      onClick={() => { frForm.resetFields(); setFrOpen(true) }}>
                      Add reason
                    </Button>
                  )}
                </Space>
                <DataTable
                  rowKey="id"
                  loading={frLoading}
                  dataSource={failureReasons}
                  columns={[
                    { title: 'Category', dataIndex: 'category', key: 'cat', width: 130 },
                    { title: 'Reason', dataIndex: 'reason_name', key: 'rn' },
                    {
                      title: 'Severity', dataIndex: 'severity', key: 'sev', width: 110,
                      render: (v: string) => <Tag color={
                        v === 'CRITICAL' ? 'red' : v === 'HIGH' ? 'orange' : 'default'
                      }>{v}</Tag>,
                    },
                    { title: 'Root cause group', dataIndex: 'root_cause_group', key: 'rcg' },
                    { title: 'Suggested action', dataIndex: 'suggested_action', key: 'sa' },
                  ]}
                />
              </>
            ),
          },
          {
            key: 'sp', label: 'Spare master',
            children: (
              <>
                <Space style={{ marginBottom: 12 }}>
                  {canCreate && (
                    <Button icon={<PlusOutlined />} type="primary"
                      onClick={() => { spForm.resetFields(); spForm.setFieldsValue({ uom: 'NOS', active: true }); setSpOpen(true) }}>
                      Add spare
                    </Button>
                  )}
                  <Input.Search
                    allowClear
                    placeholder="Search by code / name / drawing no"
                    style={{ maxWidth: 360 }}
                    onSearch={(v) => setSpSearch(v)}
                    onChange={(e) => { if (!e.target.value) setSpSearch('') }}
                  />
                </Space>
                <DataTable
                  rowKey="id" loading={spLoading} dataSource={spares}
                  columns={[
                    { title: 'Code', dataIndex: 'spare_code', key: 'c', width: 130 },
                    { title: 'Name', dataIndex: 'spare_name', key: 'n' },
                    { title: 'Drawing No', dataIndex: 'drawing_no', key: 'dwg', width: 130 },
                    { title: 'Category', dataIndex: 'category', key: 'cat', width: 120 },
                    { title: 'UOM', dataIndex: 'uom', key: 'u', width: 80 },
                    { title: 'Stock', dataIndex: 'current_stock', key: 'cs', width: 90 },
                    { title: 'Reorder', dataIndex: 'reorder_level', key: 'rl', width: 100 },
                    {
                      title: 'Avg Rate', dataIndex: 'average_rate', key: 'ar', width: 110,
                      render: (v: string | number) => `₹ ${Number(v).toFixed(2)}`,
                    },
                    {
                      title: 'Critical', dataIndex: 'critical_spare', key: 'cs2', width: 90,
                      render: (v: boolean) => v ? <Tag color="orange">YES</Tag> : null,
                    },
                  ]}
                />
              </>
            ),
          },
          {
            key: 'tm', label: 'PM templates',
            children: (
              <>
                <Space style={{ marginBottom: 12 }}>
                  {canCreate && (
                    <Button icon={<PlusOutlined />} type="primary"
                      onClick={() => { tmForm.resetFields(); tmForm.setFieldsValue({ frequency: 'DAILY', estimated_duration_minutes: 30 }); setTmOpen(true) }}>
                      Add template
                    </Button>
                  )}
                </Space>
                <DataTable
                  rowKey="id" loading={tmLoading} dataSource={templates}
                  columns={[
                    { title: 'Code', dataIndex: 'template_code', key: 'tc', width: 140 },
                    { title: 'Name', dataIndex: 'template_name', key: 'tn' },
                    { title: 'Machine type', dataIndex: 'machine_type', key: 'mt', width: 150 },
                    { title: 'Frequency', dataIndex: 'frequency', key: 'fr', width: 120 },
                    {
                      title: 'Items', dataIndex: 'checklist_json', key: 'ci', width: 80,
                      render: (v: unknown[]) => v?.length ?? 0,
                    },
                    {
                      title: 'Safety', dataIndex: 'safety_critical', key: 'sf', width: 90,
                      render: (v: boolean) => v ? <Tag color="red">SAFETY</Tag> : null,
                    },
                  ]}
                />
              </>
            ),
          },
        ]}
      />

      {/* Failure reason modal */}
      <Modal
        title="Add failure reason"
        open={frOpen}
        onOk={() => frForm.validateFields().then((v) => frMutation.mutate(v))}
        onCancel={() => setFrOpen(false)}
        confirmLoading={frMutation.isPending}>
        <Form form={frForm} layout="vertical">
          <Form.Item name="category" label="Category" rules={[{ required: true }]}>
            <Select options={FAILURE_CATEGORIES.map((c) => ({ label: c, value: c }))} />
          </Form.Item>
          <Form.Item name="reason_name" label="Reason name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="severity" label="Severity">
            <Select options={SEVERITIES.map((s) => ({ label: s, value: s }))} />
          </Form.Item>
          <Form.Item name="root_cause_group" label="Root cause group">
            <Input />
          </Form.Item>
          <Form.Item name="suggested_action" label="Suggested action">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Spare modal */}
      <Modal
        title="Add spare"
        open={spOpen}
        onOk={() => spForm.validateFields().then((v) => spMutation.mutate(v))}
        onCancel={() => setSpOpen(false)}
        confirmLoading={spMutation.isPending}
        width={560}>
        <Form form={spForm} layout="vertical">
          <Form.Item name="spare_code" label="Spare code" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="spare_name" label="Spare name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="category" label="Category"><Input placeholder="MECHANICAL / ELECTRICAL ..." /></Form.Item>
          <Form.Item name="uom" label="UOM" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="current_stock" label="Current stock"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="reorder_level" label="Reorder level"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="min_stock" label="Min stock"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="max_stock" label="Max stock"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="average_rate" label="Average rate (₹)"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="critical_spare" label="Critical spare?" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>

      {/* PM template modal */}
      <Modal
        title="Add PM template"
        open={tmOpen}
        onOk={() => tmForm.validateFields().then((v) => tmMutation.mutate(v))}
        onCancel={() => setTmOpen(false)}
        confirmLoading={tmMutation.isPending}
        width={620}>
        <Form form={tmForm} layout="vertical">
          <Form.Item name="template_code" label="Template code" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="template_name" label="Template name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="machine_type" label="Machine type" rules={[{ required: true }]}>
            <Select showSearch options={MACHINE_TYPES.map((m) => ({ label: m, value: m }))} />
          </Form.Item>
          <Form.Item name="frequency" label="Frequency" rules={[{ required: true }]}>
            <Select options={FREQUENCIES.map((f) => ({ label: f, value: f }))} />
          </Form.Item>
          <Form.Item name="estimated_duration_minutes" label="Estimated duration (min)">
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="safety_critical" label="Safety critical?" valuePropName="checked"><Switch /></Form.Item>
          <Form.Item
            name="checklist_text"
            label="Checklist items (one per line — each becomes a Yes/No check)">
            <Input.TextArea rows={6} placeholder={'Belt OK\nGuard in place\nLubrication done'} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
